import { useMutation, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { isDesktop } from "@/helpers/desktop";
import { listSerialPorts, printBarcodeLabels } from "@/helpers/native";
import useLocalStorage from "./local-storage";

/**
 * The Electron build hardcoded `COM2`, so label printing only ever worked on a
 * till wired exactly like the first one. The port is now a setting.
 */
export const DEFAULT_LABEL_PORT = "COM2";

export const useLabelPort = () =>
	useLocalStorage("labelPrinterPort", DEFAULT_LABEL_PORT);

export const useSerialPorts = () =>
	useQuery({
		queryFn: () => listSerialPorts(),
		queryKey: ["serialPorts"],
		enabled: isDesktop(),
	});

/** Narrows a product and store down to the five fields an EPL2 label uses. */
export const toBarcodeLabel = (
	product: Product,
	store: Store,
	count: number,
): BarcodeLabel => ({
	sku: product.sku,
	amountCents: product.amountCents,
	name: product.name,
	manufacturerSku: product.manufacturerSku,
	storeName: store.name,
	count,
});

/**
 * Prints barcode labels.
 *
 * The whole batch is one call: the printer has no flow control, and the pacing
 * between labels is handled on the Rust side. The Electron build sent one IPC
 * call per product and slept in the renderer in between.
 */
export const usePrintLabels = () => {
	const { t } = useTranslation();
	const [port] = useLabelPort();

	return useMutation({
		mutationFn: (labels: BarcodeLabel[]) => printBarcodeLabels(port, labels),
		onSuccess: () => {
			toast.success(t("settings.printSuccess"));
		},
		// The Electron handler crashed the main process when the port could not be
		// opened, so nothing here ever had to report a failure. Now it can.
		onError: () => {
			toast.error(t("settings.printError"));
		},
	});
};
